import { FormEvent, useEffect, useMemo, useState } from 'react'
import { WORK_UNITS } from '../../lib/dailyContext'
import {
  closeHighlight,
  createHighlight,
  deleteHighlight,
  loadHighlightManagement,
  loadHighlightUsers,
  type HighlightManagement,
  type HighlightMode,
  type HighlightUser,
} from '../../lib/highlights'

type DraftItem = { userId: string; reason: string }
type PreviewItem = { userId: string; nome: string; unit: string; reason: string; position: number }

const EMPTY_ITEMS: DraftItem[] = [
  { userId: '', reason: '' },
  { userId: '', reason: '' },
  { userId: '', reason: '' },
]

const POSITION_ICON: Record<number, string> = { 1: '🥇', 2: '🥈', 3: '🥉' }

function unitLabel(key: string | null) {
  if (!key) return 'Unidade não definida'
  return WORK_UNITS.find((unit) => unit.key === key)?.label ?? key
}

function formatDate(value: string | null) {
  if (!value) return 'Sem data final'
  return new Date(value).toLocaleString('pt-BR')
}

function formatShortDate(value: string) {
  return new Date(value).toLocaleDateString('pt-BR', { month: 'short', year: 'numeric' })
}

function modeLabel(mode: HighlightMode) {
  return mode === 'ranking' ? 'Ranking' : 'Reconhecimento'
}

function toLocalInput(value: string | null) {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  const offset = date.getTimezoneOffset() * 60_000
  return new Date(date.getTime() - offset).toISOString().slice(0, 16)
}

function HighlightPreview({ title, subtitle, mode, items, label = 'Pré-visualização' }: {
  title: string
  subtitle: string
  mode: HighlightMode
  items: PreviewItem[]
  label?: string
}) {
  const isRanking = mode === 'ranking'
  return (
    <section className={`highlight-preview highlights-banner highlights-banner--${Math.max(1, Math.min(items.length, 3))} ${isRanking ? 'is-ranking' : 'is-recognition'}`} aria-label={label}>
      <header className="highlights-banner__head">
        <div className="highlights-banner__trophy" aria-hidden="true">🏆</div>
        <div className="highlights-banner__heading">
          <p className="eyebrow">{isRanking ? 'RANKING EM DESTAQUE' : 'RECONHECIMENTO EM DESTAQUE'}</p>
          <h2>{title.trim() || 'Título do destaque'}</h2>
          <p>{subtitle.trim() || 'Sua mensagem de reconhecimento aparecerá aqui.'}</p>
        </div>
        <span className="highlights-banner__new">NOVO</span>
      </header>
      {items.length > 0 ? (
        <div className="highlights-banner__grid">
          {items.map((item) => (
            <article className={`highlight-person ${isRanking ? `highlight-person--${item.position}` : 'highlight-person--recognition'}`} key={`${item.userId}-${item.position}`}>
              <div className="highlight-person__medal" aria-hidden="true">{isRanking ? (POSITION_ICON[item.position] ?? '🏅') : '🏆'}</div>
              <div className="highlight-person__copy">
                <span className="highlight-person__position">{isRanking ? `${item.position}º lugar` : 'Destaque'}</span>
                <strong>{item.nome}</strong>
                <small>{item.unit}</small>
                <p>{item.reason || 'Motivo do reconhecimento'}</p>
              </div>
            </article>
          ))}
        </div>
      ) : <div className="highlight-preview__empty">Selecione ao menos um colaborador para visualizar os cards.</div>}
    </section>
  )
}

export function HighlightsAdmin() {
  const [users, setUsers] = useState<HighlightUser[]>([])
  const [rows, setRows] = useState<HighlightManagement[]>([])
  const [title, setTitle] = useState('')
  const [subtitle, setSubtitle] = useState('')
  const [mode, setMode] = useState<HighlightMode>('recognition')
  const [startsAt, setStartsAt] = useState('')
  const [endsAt, setEndsAt] = useState('')
  const [items, setItems] = useState<DraftItem[]>(EMPTY_ITEMS)
  const [creating, setCreating] = useState(false)
  const [viewing, setViewing] = useState<HighlightManagement | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')

  async function load() {
    setLoading(true)
    try {
      const [userRows, highlightRows] = await Promise.all([loadHighlightUsers(), loadHighlightManagement()])
      setUsers(userRows)
      setRows(highlightRows)
      setError('')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível carregar os destaques.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { void load() }, [])

  const activeCount = useMemo(() => rows.filter((row) => row.status === 'ativo').length, [rows])
  const previewItems = useMemo<PreviewItem[]>(() => items.flatMap((item, index) => {
    if (!item.userId) return []
    const user = users.find((candidate) => candidate.id === item.userId)
    if (!user) return []
    return [{
      userId: user.id,
      nome: user.nome || user.email,
      unit: unitLabel(user.default_unit_key),
      reason: item.reason.trim(),
      position: index + 1,
    }]
  }), [items, users])

  function changeItem(index: number, patch: Partial<DraftItem>) {
    setItems((current) => current.map((item, itemIndex) => itemIndex === index ? { ...item, ...patch } : item))
  }

  function resetDraft() {
    setTitle('')
    setSubtitle('')
    setMode('recognition')
    setStartsAt('')
    setEndsAt('')
    setItems(EMPTY_ITEMS.map((item) => ({ ...item })))
  }

  function duplicate(row: HighlightManagement) {
    const ordered = row.items.slice().sort((a, b) => a.position - b.position)
    setTitle(row.title)
    setSubtitle(row.subtitle ?? '')
    setMode(row.mode)
    setStartsAt('')
    setEndsAt('')
    setItems(EMPTY_ITEMS.map((_, index) => ordered[index]
      ? { userId: ordered[index].user_id, reason: ordered[index].reason }
      : { userId: '', reason: '' }))
    setViewing(null)
    setCreating(true)
    setError('')
    setMessage('Cópia carregada. Revise a vigência e os colaboradores antes de publicar.')
    window.setTimeout(() => document.querySelector('.highlight-form-card')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 0)
  }

  async function submit(event: FormEvent) {
    event.preventDefault()
    const selected = items.filter((item) => item.userId && item.reason.trim())
    if (selected.length < 1 || selected.length > 3) {
      setError('Selecione de 1 a 3 colaboradores e informe o motivo de cada destaque.')
      return
    }
    if (new Set(selected.map((item) => item.userId)).size !== selected.length) {
      setError('O mesmo colaborador não pode aparecer mais de uma vez no mesmo destaque.')
      return
    }

    setSaving(true)
    setError('')
    setMessage('')
    try {
      await createHighlight({
        title,
        subtitle,
        mode,
        startsAt: startsAt ? new Date(startsAt).toISOString() : null,
        endsAt: endsAt ? new Date(endsAt).toISOString() : null,
        items: selected.map((item, index) => ({ userId: item.userId, reason: item.reason.trim(), position: index + 1 })),
      })
      resetDraft()
      setCreating(false)
      setMessage('Destaque publicado com sucesso.')
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível publicar o destaque.')
    } finally {
      setSaving(false)
    }
  }

  async function close(id: number) {
    if (!window.confirm('Encerrar este destaque? Ele deixará de aparecer no painel dos colaboradores.')) return
    setSaving(true)
    try {
      await closeHighlight(id)
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível encerrar o destaque.')
    } finally {
      setSaving(false)
    }
  }

  async function remove(row: HighlightManagement) {
    if (row.status !== 'encerrado') {
      setError('Encerre o destaque antes de excluí-lo definitivamente do histórico.')
      return
    }

    const confirmed = window.confirm(
      `Excluir definitivamente “${row.title}” do histórico?\n\nEsta ação é irreversível e removerá também os colaboradores vinculados a esta publicação.`,
    )
    if (!confirmed) return

    setSaving(true)
    setError('')
    setMessage('')
    try {
      await deleteHighlight(row.id)
      if (viewing?.id === row.id) setViewing(null)
      setMessage('Histórico excluído definitivamente.')
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível excluir definitivamente o histórico.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <section className="highlights-admin">
      <div className="admin-section-title">
        <div>
          <p className="eyebrow">RECONHECIMENTO</p>
          <h2>Destaques</h2>
          <p className="muted">Publique reconhecimentos visíveis no topo do Atendimento para até três colaboradores.</p>
        </div>
        <button className="primary-button compact" type="button" onClick={() => setCreating((value) => !value)}>{creating ? 'Fechar' : '+ Novo destaque'}</button>
      </div>

      <div className="editor-summary highlights-summary">
        <article><span>Ativos</span><strong>{activeCount}</strong><small>destaques em exibição</small></article>
        <article><span>Colaboradores</span><strong>{users.length}</strong><small>usuários ativos elegíveis</small></article>
        <article><span>Histórico</span><strong>{rows.length}</strong><small>publicações registradas</small></article>
      </div>

      {message && <div className="notice success">{message}</div>}
      {error && <div className="notice error">{error}</div>}

      {creating && (
        <section className="editor-form-card highlight-form-card">
          <form className="editor-form" onSubmit={submit}>
            <label>Título do destaque<input value={title} maxLength={60} onChange={(e) => setTitle(e.target.value)} placeholder="Ex.: Destaques do Mês" required /><small className="field-counter">{title.length}/60</small></label>
            <label>Mensagem de apoio<input value={subtitle} maxLength={140} onChange={(e) => setSubtitle(e.target.value)} placeholder="Opcional" /><small className="field-counter">{subtitle.length}/140</small></label>

            <div className="span-2 highlight-mode-picker" role="radiogroup" aria-label="Formato do destaque">
              <button type="button" className={mode === 'recognition' ? 'active' : ''} onClick={() => setMode('recognition')}>
                <span>🏆</span>
                <div><strong>Reconhecimento</strong><small>Todos possuem o mesmo destaque, sem classificação.</small></div>
              </button>
              <button type="button" className={mode === 'ranking' ? 'active' : ''} onClick={() => setMode('ranking')}>
                <span>🥇</span>
                <div><strong>Ranking</strong><small>Exibe 1º, 2º e 3º lugar quando houver competição.</small></div>
              </button>
            </div>

            <label>Início<input type="datetime-local" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} /></label>
            <label>Fim<input type="datetime-local" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} /></label>

            <div className={`span-2 highlight-slots ${mode === 'recognition' ? 'is-recognition' : 'is-ranking'}`}>
              {items.map((item, index) => (
                <article className="highlight-slot" key={index}>
                  <div className="highlight-slot__position">
                    {mode === 'ranking' ? (POSITION_ICON[index + 1] ?? '🏅') : '🏆'}
                    <span>{mode === 'ranking' ? `${index + 1}º lugar` : 'Destaque'}</span>
                  </div>
                  <label>Colaborador<select value={item.userId} onChange={(e) => changeItem(index, { userId: e.target.value })} required={index === 0}><option value="">{index === 0 ? 'Selecione...' : 'Não utilizar este espaço'}</option>{users.map((user) => <option key={user.id} value={user.id}>{user.nome || user.email} · {unitLabel(user.default_unit_key)}</option>)}</select></label>
                  <label>Motivo<input value={item.reason} maxLength={100} onChange={(e) => changeItem(index, { reason: e.target.value })} placeholder="Ex.: Excelência no atendimento" required={Boolean(item.userId)} /><small className="field-counter">{item.reason.length}/100</small></label>
                </article>
              ))}
            </div>

            <div className="notice info span-2">{mode === 'ranking' ? 'No modo Ranking, a ordem dos colaboradores representa 1º, 2º e 3º lugar.' : 'No modo Reconhecimento, todos os colaboradores recebem o mesmo peso visual. A ordem serve apenas para organizar os cards na tela.'}</div>

            <div className="span-2 highlight-preview-wrap">
              <div className="highlight-preview-title"><div><p className="eyebrow">PRÉ-VISUALIZAÇÃO</p><strong>Como aparecerá no Atendimento</strong></div><small>O selo NOVO é exibido nas primeiras 24h da vigência.</small></div>
              <HighlightPreview title={title} subtitle={subtitle} mode={mode} items={previewItems} />
            </div>

            <div className="form-actions span-2"><button className="primary-button compact" disabled={saving}>{saving ? 'Publicando...' : 'Publicar destaque'}</button></div>
          </form>
        </section>
      )}

      {loading ? <div className="empty-state">Carregando destaques...</div> : (
        <div className="highlight-admin-list">
          {rows.length === 0 && <div className="empty-state">Nenhum destaque publicado.</div>}
          {rows.map((row) => (
            <article className="highlight-admin-card" key={row.id}>
              <div className="highlight-admin-card__head">
                <div className="highlight-admin-card__identity">
                  <div className="highlight-admin-card__status"><span className={`status-chip ${row.status === 'ativo' ? 'is-active' : ''}`}>{row.status}</span><span>{modeLabel(row.mode)}</span></div>
                  <strong>{row.title}</strong>
                  <small>{formatShortDate(row.created_at)} · {row.items.length} {row.items.length === 1 ? 'colaborador' : 'colaboradores'} · publicado por {row.created_by_name || 'Gestão'}</small>
                </div>
                <div className="highlight-admin-card__actions">
                  <button className="secondary-button compact" type="button" onClick={() => setViewing(row)}>Ver</button>
                  <button className="secondary-button compact" type="button" onClick={() => duplicate(row)}>Duplicar</button>
                  {row.status === 'ativo' && <button className="secondary-button compact" type="button" disabled={saving} onClick={() => void close(row.id)}>Encerrar</button>}
                  {row.status === 'encerrado' && <button className="secondary-button compact highlight-delete-button" type="button" disabled={saving} onClick={() => void remove(row)}>Excluir</button>}
                </div>
              </div>
              {row.subtitle && <p>{row.subtitle}</p>}
              <div className="highlight-admin-card__period"><span>Vigência</span><strong>{formatDate(row.starts_at)} → {formatDate(row.ends_at)}</strong></div>
            </article>
          ))}
        </div>
      )}

      {viewing && (
        <div className="highlight-history-modal" role="dialog" aria-modal="true" aria-label={`Visualizar ${viewing.title}`} onMouseDown={(event) => { if (event.target === event.currentTarget) setViewing(null) }}>
          <div className="highlight-history-modal__panel">
            <div className="highlight-history-modal__head">
              <div><p className="eyebrow">PUBLICAÇÃO ORIGINAL</p><h3>{viewing.title}</h3><small>{modeLabel(viewing.mode)} · {formatDate(viewing.starts_at)} → {formatDate(viewing.ends_at)}</small></div>
              <button className="secondary-button compact" type="button" onClick={() => setViewing(null)}>Fechar</button>
            </div>
            <HighlightPreview
              title={viewing.title}
              subtitle={viewing.subtitle ?? ''}
              mode={viewing.mode}
              label={`Publicação original: ${viewing.title}`}
              items={viewing.items.slice().sort((a, b) => a.position - b.position).map((item) => ({ userId: item.user_id, nome: item.nome, unit: item.unit_label, reason: item.reason, position: item.position }))}
            />
            <div className="highlight-history-modal__actions"><button className="primary-button compact" type="button" onClick={() => duplicate(viewing)}>Duplicar esta publicação</button></div>
          </div>
        </div>
      )}
    </section>
  )
}
